<script setup lang="ts">
import {fetchProductsData, fetchRelatedData, type InfoData} from '~/queries'
import {fetchApi, queryKey} from '~/queries/helpers'
import {cleanPath} from '~/utils'
import MatchList from '~/components/football/MatchList.vue'
import ClubInfoSection from '~/components/football/ClubInfoSection.vue'
import ViewBanner from '~/components/football/ViewBanner.vue'
import {useJsonLdStore} from "~/stores/jsonld";
import UiAccordion from '@boats/ui/components/UiAccordion.vue'

const props = defineProps<{
  breadcrumbs: InfoData['breadcrumbs']
  page: InfoData['page']
  refresh: () => Promise<unknown>
}>()

const route = useRoute()
const site = useSiteStore()
const { $i18n } = useNuxtApp()
const { t } = $i18n

// Storefront view (BOAT-1912): `?view=<slug>` narrows the fixture list to
// fixtures with categories carrying that label, swaps in the partner logo and
// carries the param into every fixture link. URL only — nothing persisted.
const { activeView, offeredViews, viewQuery, hrefForView, exitHref } = useStorefrontView(() => props.page?.views)

function getProductsPerPage() {
  if (props?.page?.templateAlias === 'predefined_football_search_page') {
    return 30
  } else {
    return props.page?.hasChild ? 30: 100
  }
}

const productsPerPage = getProductsPerPage()
const currentPage = computed(() => parseInt((route.query.page || 1) as string))
const offset = computed(() => (currentPage.value - 1) * productsPerPage)
usePaginationCanonical(currentPage.value)

//const response = await fetchApi(`/pages/${props.page.id}/filters`)
const filters = ref({
  dates:  [],
  venues: [],
  tags: [],
  selectedDate: null,
  selectedVenue: null,
  selectedTags: [],
})

const productsFilters = computed(() => {
  if (!props.page) return

  const search: Record<string, unknown> = {
    'search[current]': 1,
    'orderByRaw': 'start_date,ASC',
  }
  if (activeView.value) {
    search['search[view]'] = activeView.value.slug
  }
  if (props.page.templateAlias === 'predefined_football_search_page' && props.page.predefinedSearchCriteriaOptionId) {
    search['search[tag_filter]'] = props.page.predefinedSearchCriteriaOptionId;
  } else {
    search['search[page-descendants]'] = props.page.id;

    // if (filters.value.selectedTags) {
    //   search['search[tags][]'] = filters.value.selectedTags.map(tag => tag.id)
    // }
    //
    // if (filters.value.selectedDate) {
    //   search['search[date]'] = filters.value.selectedDate.value
    // }
    //
    // if (filters.value.selectedVenue) {
    //   search['search[venue]'] = filters.value.selectedVenue.value
    // }
  }

  return search
})

const faqs = computed(() => {
  const faqsArray = []

  if (props?.page?.templateAlias === 'category_football_club') {
    if (props.page?.additionalNotes) {
      faqsArray.push({
        title: t('page.product.additionalNotes.title.football'),
        content: props.page.additionalNotes
      })
    }

    if (props.page?.additionalDescription) {
      faqsArray.push({
        title: t('page.product.additionalDescription.title'),
        content: props.page.additionalDescription
      })
    }
  }

  return faqsArray
})

const authToken = useCookie('authToken')

const [productsResult, relatedResult] = await Promise.all([
  useAsyncData(
    queryKey('products', productsPerPage, offset.value, productsFilters.value),
    async () => {
      if (!productsPerPage || !productsFilters.value) return

      const data = await fetchProductsData({
        limit: productsPerPage,
        offset: offset.value,
        ...productsFilters.value
      })

      if ((!data || data.items.length === 0) && route.query.page) {
        throw createError({ statusCode: 404, statusMessage: 'Not Found' })
      }

      return data;
    },
    { watch: [authToken, offset, productsFilters] }
  ),
  useAsyncData(
    queryKey('related', props.page?.id),
    async () => {
      if (!props.page?.id) return

      return await fetchRelatedData({ id: props.page.id })
    },
    { watch: [authToken] }
  )
])

if (productsResult.error.value?.statusCode === 404) {
  throw productsResult.error.value
}

const {
  data: productsData,
  pending: productsPending,
  refresh: productsRefresh
} = productsResult

const { data: relatedData, refresh: relatedRefresh } = relatedResult

const breadcrumbsItems = computed(() =>
  props.breadcrumbs.map((item) => ({
    title: item.title,
    href: cleanPath('/', item.path)
  }))
)

const products = computed(() => productsData.value?.items)
const totalProducts = computed(() => productsData.value?.total)
const hasProducts = ref(totalProducts.value > 0)

const related = computed(() => relatedData.value?.items)

const pagination = computed(() => {
  const totalPages = productsData.value?.total
    ? Math.ceil(productsData.value?.total / productsPerPage)
    : 1

  const previousPage = Math.max(currentPage.value - 1, 1)
  const nextPage = Math.min(currentPage.value + 1, totalPages)

  const path = cleanPath(route.path)
  // Keep the active storefront view across pages of the list.
  const viewParam = activeView.value ? `view=${encodeURIComponent(activeView.value.slug)}` : ''
  const withQuery = (params: string[]) => {
    const query = params.filter(Boolean).join('&')
    return query ? `${path}?${query}` : path
  }

  const previousHref = withQuery([previousPage > 1 ? `page=${previousPage}` : '', viewParam])
  const nextHref = withQuery([`page=${nextPage}`, viewParam])

  return {
    current: currentPage.value,
    total: totalPages,
    previousHref,
    nextHref
  }
})

const scrollToTop = () => {
  const top = (document.getElementById('#products')?.offsetTop || 80) - 80

  window.scrollTo({ top: top || 0, behavior: 'smooth' })
}

const jsonStore = useJsonLdStore()
if (products.value && products.value.length) {
  products.value.forEach((product) => {
    jsonStore.addSportEvent(product, site);
  })
}
</script>

<template>
  <AppContainer v-if="page" class="my-10 md:my-20">
    <section class="club-overview">
      <div class="club-overview__image-block">
        <AppCarouselMedia :object="page" type="page" />
        <h1 class="club-overview__title only-mobile">{{ page.title }}</h1>
        <img
          v-if="activeView?.logoUrl"
          :src="activeView.logoUrl"
          :alt="activeView.name || ''"
          class="club-overview__partner-logo only-mobile"
        />
        <div class="club-overview__guarantees">
          <ul class="club-overview__guarantees-list">
            <li class="club-overview__guarantees-item">
              <div class="club-overview__guarantees-card">
                <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 3.74979V10.5C0 17.6927 8.4175 20.8278 8.7789 20.9553C8.94431 21.0149 9.12545 21.0149 9.29087 20.9553C9.65227 20.8278 18.0698 17.6927 18.0698 10.5V3.74979C18.0697 3.59565 18.022 3.44528 17.933 3.31913C17.8441 3.19298 17.7184 3.09718 17.5728 3.04477L9.29087 0.0446823C9.12545 -0.0148941 8.94431 -0.0148941 8.7789 0.0446823L0.496918 3.04477C0.351404 3.09718 0.225632 3.19298 0.136718 3.31913C0.0478038 3.44528 6.58339e-05 3.59565 0 3.74979Z"
                    fill="#408A13" />
                  <path d="M5.85986 9.68243L7.94901 11.7201L12.6971 6.34802" stroke="white" stroke-width="1.5" />
                </svg>
                <div>
                  <span class="club-overview__guarantees-card-title">{{ $t('page.category-football.guarantee.title') }}</span>
                  <p class="club-overview__location-description only-desktop">
                    {{ $t('page.category-football.guarantee.description') }}</p>
                </div>
              </div>
            </li>
            <li class="club-overview__guarantees-item">
              <div class="club-overview__guarantees-card">
                <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M10.5313 3.07583V9.34875H18.1795L17.3418 18.5363H7.89845V7.45084L10.5313 3.07583ZM5.29138 6.72375C5.30713 6.66933 5.32994 6.6169 5.35943 6.56788L9.02306 0.479864C9.14206 0.282202 9.35638 0.161255 9.58754 0.161255H12.5059C12.8693 0.161255 13.1641 0.455071 13.1641 0.817505V6.72375H20.3418C20.7288 6.72375 21.0323 7.05511 20.9972 7.43942L19.8005 20.5645C19.7697 20.9024 19.4855 21.1613 19.1451 21.1613H7.24025H5.92384H0.658204C0.294691 21.1613 0 20.8674 0 20.505V7.38C0 7.01757 0.294691 6.72375 0.658204 6.72375H5.29138ZM5.26564 9.34875H2.63282V18.5363H5.26564V9.34875Z"
                        fill="#408A13" />
                </svg>
                <div>
                  <span class="club-overview__guarantees-card-title">{{ $t('page.category-football.guarantee2.title')
                    }}</span>
                  <p class="club-overview__location-description only-desktop">
                    {{ $t('page.category-football.guarantee2.description') }}</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="club-overview__content-block">
        <div class="club-overview__header only-desktop">
          <AppBreadcrumbs class="club-overview__breadcrumb" :items="breadcrumbsItems" />
          <h1 class="club-overview__title">{{ page.title }}</h1>
          <img
            v-if="activeView?.logoUrl"
            :src="activeView.logoUrl"
            :alt="activeView.name || ''"
            class="club-overview__partner-logo"
          />
        </div>
        <!-- Storefront views (BOAT-1912): offer each available view on the default
             page; offer the way back while one is active. -->
        <div v-if="offeredViews.length || activeView" class="club-overview__view-banners">
          <ViewBanner
            v-for="view in offeredViews"
            :key="view.slug"
            :title="view.bannerTitle"
            :body="view.bannerBody"
            :cta="view.bannerCta || view.name || view.slug"
            :colour="view.colour"
            :href="hrefForView(view)"
          />
          <ViewBanner
            v-if="activeView"
            exit
            :title="activeView.name"
            :cta="$t('string.View-All-Fixtures')"
            :href="exitHref"
          />
        </div>
        <AppDivider id="#products" class="mt-7" />
        <MatchList
          v-if="hasProducts"
          :filters="filters"
          :products="products"
          :link-query="viewQuery"
          :title="`${$t('page.category-football.products').toUpperCase()}${totalProducts && totalProducts > 0 ? ` (${totalProducts})` : ''}`"
        />
        <article v-else class="cms-content mt-10" v-html="page.description"></article>
        <AppPagination
          v-if="pagination.total > 1"
          class="lg:mt-8 mt-0"
          :current="pagination.current"
          :total="pagination.total"
          :previous-href="pagination.previousHref"
          :next-href="pagination.nextHref"
          :previous-disabled="pagination.current <= 1"
          :next-disabled="pagination.current >= pagination.total"
          @previous="scrollToTop"
          @next="scrollToTop"
        />
      </div>
    </section>
    <ClubInfoSection v-if="products?.length" :title="page.title">
      <div class="cms-content" v-html="page.description"></div>
    </ClubInfoSection>
    <section class="row">
      <div class="col-lg-3 mb-6 lg:mb-0">
        <h2 class="section__title">{{page.title}} FAQ</h2>
      </div>
      <div class="col-lg-9 faq-list">
        <UiAccordion v-if="faqs.length" :items="faqs" />
      </div>
    </section>
    <AppDivider class="my-8 md:my-16" strong />
    <AppNewsletter />
  </AppContainer>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.club-overview {
  a {
    text-decoration: none;
  }

  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 42px;
  padding-bottom: 36px;

  @include media-breakpoint-up(lg) {
    padding-bottom: 100px;
    grid-template-columns: 400px 1fr;
  }

  &__image-block {
    display: grid;
    grid-row-gap: 12px;
    margin-bottom: 12px;

    @include media-breakpoint-up(lg) {
      grid-auto-rows: max-content;
      margin-bottom: 0;
    }
  }

  &__title {
    @include h1-poppins;
    color: $black;

    @include media-breakpoint-up(lg) {
      margin-bottom: 20px;
    }
  }

  &__location {
    &-name {
      @include label-secondary-nav;
      margin: 0 21px 0 12px;
      color: $gray-400;
    }

    &-link {
      display: flex;
      justify-content: left;
      align-items: center;
      text-decoration: none;

      svg {
        display: inline;
      }

      & > svg {
        width: 20px;
        height: 20px;

        & > path {
          fill: $gray-400;

          @include media-breakpoint-up(lg) {
            fill: $black;
          }
        }
      }
    }

    &-description {
      font-family: Poppins, sans-serif;
      font-weight: 400;
      font-size: 13px;
      line-height: 24px;
      color: $gray-400;
    }
  }

  &__guarantees {
    &-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 12px;
      list-style-type: none;
      padding-left: 0;

      @include media-breakpoint-up(lg) {
        grid-template-columns: 1fr;
      }
    }

    &-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      height: 100%;
      padding: 12px 26px;
      border-radius: 4px;
      border: 1px solid $gray-200;

      @include media-breakpoint-up(lg) {
        flex-direction: row;
        align-items: start;
        gap: 16px;
        padding: 22px 24px 21px 16px;
      }

      & svg {
        width: 21px;
        height: 21px;
        color: $green;

        @include media-breakpoint-up(lg) {
          flex-shrink: 0;
          width: 24px;
          height: 27px;
        }
      }

      &-title {
        @include green-popins;
        display: block;
        text-align: center;

        @include media-breakpoint-up(lg) {
          text-align: left;
        }
      }

      &-description {
        margin-top: 12px;
        font-family: 'Poppins', sans-serif;
        font-style: normal;
        font-weight: 400;
        font-size: 13px;
        line-height: 24px;
        color: $gray-400;
      }
    }
  }

  &__breadcrumb {
    @include label-tag;
    color: $black;
    margin-bottom: 12px;
  }

  &__desktop-title {
    @include h1-barlow;
    color: $black;
  }

  &__header {
    margin-bottom: 32px;
  }
}

.about-club {
  &__title {
    margin-bottom: 12px;
  }

  & article {
    max-width: 730px;
    margin: 0 auto;
  }
}

.section {
  padding: 14px 0 40px 0;
  display: grid;
  grid-template-columns: 1fr;

  &__title {
    width: 100%;
    @include h2-barlow;

    @include media-breakpoint-up(lg) {
      width: 270px;
    }
  }
}


/* Storefront view (BOAT-1912): partner lock-up under the H1, generous size. */
.club-overview__partner-logo {
  display: block;
  max-height: 120px;
  max-width: min(100%, 400px);
  margin: 8px 0 16px;
  object-fit: contain;

  &.only-mobile {
    margin: 12px 0 0;
    max-height: 96px;
    max-width: min(100%, 320px);
  }
}
.club-overview__view-banners {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}
</style>
